import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useLiveQuery } from '@tanstack/react-db'

import { useDb } from '@/lib/dbContext'
import { countDue, type FollowUp } from '@/db/domain/followUps'

interface FollowUpsContextValue {
  /** Open follow-ups that have come due — what the nav badge shows. */
  dueCount: number
}

// Defaulted rather than undefined so Header renders outside a provider (in
// Storybook, and in the kiosk shell) without blowing up.
const FollowUpsContext = createContext<FollowUpsContextValue>({ dueCount: 0 })

export function useFollowUps(): FollowUpsContextValue {
  return useContext(FollowUpsContext)
}

/**
 * Keeps the due-follow-up count live for the nav badge.
 *
 * Unlike feedback, this needs no polling: follow-ups are a synced collection,
 * so a live query already re-runs on every local write and refetch. The one
 * thing time alone changes is *whether* a future due date has arrived, which is
 * what the interval below covers — a follow-up due at 09:00 should light up the
 * badge at 09:00, not at the next unrelated write.
 */
export function FollowUpsProvider({ children }: { children: ReactNode }) {
  const db = useDb()
  const followUpsResult = useLiveQuery((q) =>
    q.from({ followUps: db.followUpsCollection }),
  )

  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), DUE_TICK_MS)
    return () => clearInterval(interval)
  }, [])

  const followUps = (followUpsResult.data ?? []) as FollowUp[]

  const value = useMemo(
    () => ({ dueCount: countDue(followUps, now) }),
    [followUps, now],
  )

  return (
    <FollowUpsContext.Provider value={value}>
      {children}
    </FollowUpsContext.Provider>
  )
}

// Due-ness is day-granular, so re-checking every few minutes is plenty; this is
// only here so a tab left open overnight notices the new day.
const DUE_TICK_MS = 5 * 60_000
